import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import Logo from '../../../Logo.svg?react'
import PSLogo from '../../../PocketSuite-logo.svg?react'
import { SQLiteFactory } from '../../../models/SQLiteFactory'
import { User } from '../../../models/User'
import { useNotificationListener } from '../../../providers/NotificationsManagerProvider'
import { Icon } from '../../Icon'
import { ContactAvatar } from '../../contacts/ContactAvatar'
import { AccountSwitcherModal } from '../../login/AccountSwitcherModal'
import Badge from './Badge'
import Divider from './Divider'
import Label from './Label'
import NavLink, { NavLink as NavLinkT } from './NavLink'

type SidebarProps = React.ComponentPropsWithoutRef<'aside'> & {
    links: NavLinkT[]
    user?: User
}

const Sidebar = ({ links, user, ...props }: SidebarProps) => {
    const queryClient = useQueryClient()
    const { data: roleBadgeCount } = useQuery({
        queryKey: ['role-badge-count'],
        queryFn: async () => (await SQLiteFactory.getInstance().roleBadgeCount()) ?? 0,
    })

    const navLinksContainerRef = useRef<HTMLDivElement>(null)

    const [collapsed, setCollapsed] = useState(window.innerWidth <= 1380)
    const [areNavLinksScrolling, setAreNavLinksScrolling] = useState(false)
    const [isAccountSwitcherModalOpen, setIsAccountSwitcherModalOpen] = useState(false)

    useNotificationListener('MessagesTabDataSourceChanged', () =>
        queryClient.invalidateQueries({ queryKey: ['role-badge-count'] })
    )

    useEffect(() => {
        const handleResize = () => {
            setCollapsed(window.innerWidth <= 1380)
            setAreNavLinksScrolling(
                navLinksContainerRef.current !== null &&
                    navLinksContainerRef.current.scrollHeight >
                        navLinksContainerRef.current.clientHeight
            )
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return (
        <aside
            className={twm(
                'flex flex-col border-r-2 border-gray-200 bg-white px-4 transition-all duration-300 ease-in-out',
                collapsed ? 'w-[92px]' : 'w-[248px]'
            )}
            {...props}
        >
            {!collapsed ? (
                <Logo
                    className="my-6 shrink-0 self-center"
                    role="img"
                    aria-label="PocketSuite logo"
                    width="132"
                    height="44"
                />
            ) : (
                <PSLogo className="my-10 shrink-0 self-center" role="img" aria-label="PS logo" />
            )}

            {user && (
                <button
                    className={twm(
                        'flex shrink-0 items-center rounded-lg border border-gray-300 py-3 text-left text-gray-700 hover:bg-black/4',
                        collapsed ? 'gap-1 px-1.5' : 'gap-4 px-4'
                    )}
                    onClick={() => setIsAccountSwitcherModalOpen(true)}
                >
                    <div className="relative shrink-0">
                        <ContactAvatar user={user} size="xs" data-testid="contact-avatar" />
                        {roleBadgeCount !== undefined && roleBadgeCount > 0 && (
                            <Badge
                                value={roleBadgeCount}
                                className="absolute top-[-7px] right-[-7px]"
                            />
                        )}
                    </div>
                    {!collapsed && user.name && (
                        <Label className="line-clamp-2 grow" size="sm">
                            {user.type === 'payee' && user.role === 'admin'
                                ? user.name
                                : (user.entity ?? user.name)}
                        </Label>
                    )}

                    <Icon icon="chevronDown.small" className="shrink-0 text-gray-700" />
                </button>
            )}

            <Divider className="mt-4 mb-0 shrink-0" />

            <nav
                ref={navLinksContainerRef}
                className="flex shrink grow flex-col overflow-x-hidden overflow-y-auto py-4"
            >
                {links.map((link) => (
                    <NavLink key={link.to} link={link} collapsed={collapsed} />
                ))}
            </nav>

            {areNavLinksScrolling && <Divider className="mt-0" />}

            <NavLink
                link={{ to: '/logout', label: 'Log out', icon: 'logOut.large' }}
                collapsed={collapsed}
            />

            <Divider className="mt-4 mb-0 shrink-0" />

            <div className="my-6 flex shrink-0 items-center justify-center text-gray-600">
                <Label size="sm">{collapsed ? 'Beta' : 'Desktop beta'}</Label>
            </div>

            <AccountSwitcherModal
                isOpen={isAccountSwitcherModalOpen}
                onClose={() => setIsAccountSwitcherModalOpen(false)}
            />
        </aside>
    )
}

export default Sidebar
